import PropTypes from "prop-types"
import React, { useCallback, useRef, useState } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import DiscardConfirmModal from "src/components/DiscardConfirmModal"
import Modal from "src/components/Modal"

import { createJob } from "src/api/Service/Jobs"

import { useDiscardConfirm } from "src/hooks/use_discard_confirm"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import { CREATED as JOB_TEMPLATE_CREATED } from "../JobTemplates/amplitude_events"
import JobFillStep from "./JobFillStep"
import JobTemplatePicker from "./JobTemplatePicker"
import {
  ADD_JOB_TEMPLATE_CHANGED,
  CREATE_JOB_DISCARDED,
  JOB_CREATED,
} from "./amplitude_events"

const CreateJobModal = ({
  isOpen,
  onClose,
  workOrderSequentialId,
  boatLengthFeet,
  onJobCreated,
}) => {
  const { marinaSlug, presentmentCurrency } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()
  const tracker = useTracker()

  const [step, setStep] = useState("pick")
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [fillIsDirty, setFillIsDirty] = useState(false)
  const [fillIsFormOpen, setFillIsFormOpen] = useState(false)
  const [fillIsBlocked, setFillIsBlocked] = useState(false)

  const fillStepRef = useRef(null)

  const returnToPicker = useCallback(() => {
    tracker.trackEvent(ADD_JOB_TEMPLATE_CHANGED, {
      fromTemplateId: selectedTemplate?.id,
    })
    setSelectedTemplate(null)
    setStep("pick")
    setFillIsDirty(false)
    setFillIsFormOpen(false)
    setFillIsBlocked(false)
  }, [tracker, selectedTemplate])

  const { handleCloseRequest, discardConfirmProps } = useDiscardConfirm({
    isDirty: fillIsDirty,
    onClose,
  })

  const handleDiscard = useCallback(() => {
    tracker.trackEvent(CREATE_JOB_DISCARDED, {
      workOrderSequentialId,
    })
    discardConfirmProps.onDiscard()
  }, [tracker, workOrderSequentialId, discardConfirmProps])

  const {
    handleCloseRequest: handleChangeTemplate,
    discardConfirmProps: switchTemplateConfirmProps,
  } = useDiscardConfirm({
    isDirty: fillIsDirty,
    onClose: returnToPicker,
  })

  const resetAll = useCallback(() => {
    setStep("pick")
    setSelectedTemplate(null)
    setFillIsDirty(false)
    setFillIsFormOpen(false)
    setFillIsBlocked(false)
  }, [])

  const { mutate: submitJob, isLoading } = useMutation(
    (vars) => createJob(marinaSlug, workOrderSequentialId, vars),
    {
      onSuccess: (job, vars) => {
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        tracker.trackEvent(JOB_CREATED, {
          line_items_count: vars.lineItems.length,
          from_template_id: vars.templateId ?? null,
          has_labor: vars.lineItems.some((li) => li.productType === "labor"),
          has_parts: vars.lineItems.some((li) => li.productType !== "labor"),
        })
        if (vars.saveAsTemplate) {
          queryClient.invalidateQueries(["jobTemplates", marinaSlug])
          tracker.trackEvent(JOB_TEMPLATE_CREATED, {
            line_items_count: vars.lineItems.length,
            source: "save_from_wo",
          })
        }
        onJobCreated?.(job?.id)
        onClose()
        showToast(
          vars.saveAsTemplate
            ? "Job created and saved as template."
            : "Job created successfully.",
          { type: "success" }
        )
      },
      onError: (error) => {
        showToast(error.message || "Failed to create job.", { type: "error" })
      },
    }
  )

  const handleFillSubmit = useCallback(
    ({ name, description, lineItems, saveAsTemplate }) => {
      submitJob({
        name,
        description,
        templateId: selectedTemplate?.id ?? null,
        saveAsTemplate: !selectedTemplate && !!saveAsTemplate,
        lineItems,
      })
    },
    [submitJob, selectedTemplate]
  )

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleCloseRequest}
        afterLeave={resetAll}
        size="large"
      >
        <Modal.Header title="Add Job" />
        <Modal.Body>
          <div className="flex flex-col gap-4 lg:p-4">
            {step === "pick" ? (
              <JobTemplatePicker
                boatLengthFeet={boatLengthFeet}
                onSelect={(template) => {
                  setSelectedTemplate(template)
                  setStep("fill")
                }}
                onStartFromScratch={() => {
                  setSelectedTemplate(null)
                  setStep("fill")
                }}
              />
            ) : (
              <JobFillStep
                key={selectedTemplate?.id ?? "scratch"}
                ref={fillStepRef}
                template={selectedTemplate}
                boatLengthFeet={boatLengthFeet}
                presentmentCurrency={presentmentCurrency}
                onChangeTemplate={handleChangeTemplate}
                onSubmit={handleFillSubmit}
                onDirtyChange={setFillIsDirty}
                onFormOpenChange={setFillIsFormOpen}
                onBlockedChange={setFillIsBlocked}
              />
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              type="button"
              onClick={handleCloseRequest}
              disabled={isLoading}
            >
              Cancel
            </Button>
            {step === "fill" && (
              <Button
                variant="primary"
                type="button"
                onClick={() => fillStepRef.current?.submit()}
                isLoading={isLoading}
                disabled={isLoading || fillIsFormOpen || fillIsBlocked}
              >
                Add Job
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <DiscardConfirmModal {...discardConfirmProps} onDiscard={handleDiscard} />
      <DiscardConfirmModal
        {...switchTemplateConfirmProps}
        title="Switch template?"
        message="Your changes to this job will be cleared."
        discardLabel="Switch template"
      />
    </>
  )
}

CreateJobModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
  boatLengthFeet: PropTypes.number,
  onJobCreated: PropTypes.func,
}

export default CreateJobModal
